/* global umami */
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { supabase } from "./supabase";
import ALL_PUZZLES from "./puzzles.json";

if (!document.getElementById("ou-fonts")) {
  const l = document.createElement("link");
  l.id = "ou-fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Fredoka:wght@600;700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600&family=Roboto+Mono:wght@400;500&display=swap";
  document.head.appendChild(l);
}

const C = {
  bg: "#0D0D0D", surface: "#2A2210", border: "#3A3015",
  text: "#EDE8D8", muted: "#888888", accent: "#D4A832",
  green: "#3EC87A", yellow: "#D4A832", red: "#E85050",
};

const NICKNAMES = ["Skipper","Ace","Slugger","Captain","Hotshot","Maverick","Rocket","Tank","Bruiser","Sparky","Turbo","Slick","Trooper","Cobber","Stallion","Chook","Jet","Knuckles","Duke","Tex","Hoss","Pistol","Gunner","Crusher","Stretch","Pickle","Scooter","Shorty","Peanut","Nugget","Spud","Muppet","Sausage","Wombat","Banjo","Cracker","Ripper","Digger","Champ","Chief","Sport","Pal","Muscles","Big Fella","Boss","Head","Junior","Kiddo","Tiger","Big Guy","Capo","Buddy","Legend","Hero","Biscuit","Babe","Hun","Sweets"];


const TOTAL_ROUNDS = 4;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Day selection: puzzles advance one per calendar day, and clamp at the newest
// authored day — never wrapping back to Day 1. Appending puzzles just raises the
// clamp ceiling, so a new day rolls out on its own calendar day and nothing needs
// re-tuning on deploy.
//
// The schedule is a list of SEGMENTS. A segment says "puzzle day startDay falls
// on this calendar date, and days advance one per calendar day from there".
// Normally there is exactly one. A second is added only when the pool ran dry and
// the game sat on its last day while the calendar moved on: without it, the new
// batch would arrive with its first few days already in the past and never be
// served. A segment re-starts the count at the first NEW day on a chosen date.
// Append-only — earlier segments stay put, which keeps every historical day's
// archive date exactly what it was. Dates are UTC midnights built with Date.UTC()
// so DST transitions never cause an off-by-one.
const DAY_SEGMENTS = [
  { startDay: 94,  start: Date.UTC(2026, 5, 28) },  // 28 Jun 2026 is Day 94
  { startDay: 204, start: Date.UTC(2026, 9, 20) },  // pool ran dry on Day 203; Day 204 starts 20 Oct 2026
];

// Visible build marker (shown tiny in the footer). Bump on each deploy. Combined with the
// live pool size below, a stale cached bundle is obvious at a glance (old date / fewer days).
const APP_BUILD = "2026-11-05";

function getDayIndex() {
  const now = new Date();
  const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  // The segment in force is the latest one that has already started.
  let i = 0;
  DAY_SEGMENTS.forEach((s, k) => { if (s.start <= todayUTC) i = k; });
  const seg = DAY_SEGMENTS[i];
  let dayNumber = seg.startDay + Math.floor((todayUTC - seg.start) / 86400000);
  // If a later segment is queued, hold on this segment's last day rather than
  // running into the next segment's days early — that is the "stuck on the final
  // day until the new batch starts" period.
  const next = DAY_SEGMENTS[i + 1];
  if (next) dayNumber = Math.min(dayNumber, next.startDay - 1);
  // Clamp to [1, pool size] so it never wraps and never runs past the newest day.
  const clamped = Math.max(1, Math.min(ALL_PUZZLES.length, dayNumber));
  return clamped - 1;
}

const MONTH_SHORT = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];

function getPuzzleDayDate(puzzleDay) {
  // Calendar date on which a given puzzle day is/was "today", from the segment
  // that day belongs to. Days after today read as future → locked in the archive;
  // built as a LOCAL-time date to match the local-midnight isFuture check in the
  // archive grid.
  let seg = DAY_SEGMENTS[0];
  DAY_SEGMENTS.forEach(s => { if (s.startDay <= puzzleDay) seg = s; });
  const u = new Date(seg.start); // UTC midnight of the segment's start date
  const d = new Date(u.getUTCFullYear(), u.getUTCMonth(), u.getUTCDate()); // same calendar date, local midnight
  d.setDate(d.getDate() + (puzzleDay - seg.startDay));
  return d;
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.cssText = "position:fixed;top:0;left:0;width:2px;height:2px;opacity:0;pointer-events:none";
    document.body.appendChild(el);
    el.focus(); el.select(); el.setSelectionRange(0, 99999);
    let ok = false;
    try { ok = document.execCommand("copy"); } catch {}
    document.body.removeChild(el);
    return ok;
  }
}

// One share string for every screen: grid, score, and the day's quip when there
// is one. Kept plain text so it survives any app it lands in.
//
// Deliberately no link: chat apps expand a bare URL into a large preview card,
// which buries the result and reads as spam. The grid is the hook; people who
// want the game ask for it.
function buildShareText({ day, roundResults, quip }) {
  const em = { perfect: "🟩", second: "🟨", wrong: "🟥" };
  const score = roundResults.filter(r => r !== "wrong").length;
  const lines = [
    `PECKING ORDER — Day ${day}`,
    roundResults.map(r => em[r]).join(" "),
    `${score}/${TOTAL_ROUNDS}`,
  ];
  if (quip) lines.push("", `"${quip}"`);
  return lines.join("\n");
}

// Native share sheet where the browser has one (straight into WhatsApp, X,
// Messages), clipboard everywhere else. Returns what actually happened so the
// button doesn't claim "copied" after a share sheet, or after a cancel.
async function shareText(text) {
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ text });
      return "shared";
    } catch (err) {
      if (err && err.name === "AbortError") return "cancelled";
      // Anything else (permission, unsupported payload) falls through to copy.
    }
  }
  const ok = await copyToClipboard(text);
  return ok ? "copied" : "failed";
}

function ProfileIcon({ size = 22, color = C.muted }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="1.8" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function StatsBar({ streakData, daysPlayed }) {
  const current = streakData?.current_streak ?? 0;
  const longest = streakData?.longest_streak ?? 0;
  const days = daysPlayed ?? 0;
  return (
    <div style={{ display: "flex", gap: 0, marginBottom: 20, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
      {[
        { label: "Days Played", value: days },
        { label: "Current Streak", value: current },
        { label: "Longest Streak", value: longest },
      ].map((s, i) => (
        <div key={i} style={{ flex: 1, textAlign: "center", padding: "14px 8px", borderRight: i < 2 ? `1px solid ${C.border}` : "none", background: C.surface }}>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 28, fontWeight: 700, color: C.accent }}>{s.value}</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8, letterSpacing: 2, color: C.muted, textTransform: "uppercase", marginTop: 4 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function ProfileScreen({ user, streakData: initialStreakData, userResults: initialResults, onBack, onSignOut, onGoogleLogin, todayPuzzleDay, onSelectDay, dataReady }) {
  const [userResults, setUserResults] = useState(initialResults);
  const [streakData, setStreakData] = useState(initialStreakData);
  // If App already loaded data, show immediately — no spinner, no second fetch
  const [loaded, setLoaded] = useState(dataReady || initialResults.length > 0);
  const [signOutErr, setSignOutErr] = useState(null);
  const [subview, setSubview] = useState("stats"); // "stats" | "archive"
  const [archiveMonthIdx, setArchiveMonthIdx] = useState(null); // null = latest month
  const [animated, setAnimated] = useState(false);
  useEffect(() => { const id = setTimeout(() => setAnimated(true), 50); return () => clearTimeout(id); }, []);
  const handleSignOut = async () => {
    setSignOutErr(null);
    try { await onSignOut(); } catch { setSignOutErr("Sign-out failed — please try again."); }
  };

  useEffect(() => {
    if (!user) { setLoaded(true); return; }
    // App already has fresh data from startup loadUserData — use it, skip the fetch
    if (dataReady) { setLoaded(true); return; }
    // Fallback: App data not ready yet, fetch directly
    const fetchTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("[Archive] fetch timed out after 8s")), 8000)
    );
    Promise.race([
      Promise.all([
        supabase.from("results").select("day, score, round_results").eq("user_id", user.id),
        supabase.from("streaks").select("*").eq("user_id", user.id).maybeSingle(),
      ]),
      fetchTimeout,
    ]).then(([{ data: results, error: rErr }, { data: streak, error: sErr }]) => {
      if (rErr) console.error("[Archive] results fetch FAILED", rErr);
      if (sErr) console.error("[Archive] streaks fetch FAILED", sErr);
      if (results) setUserResults(results);
      if (streak) setStreakData(streak);
      setLoaded(true);
    }).catch(err => {
      console.error("[Archive] fetch failed or timed out:", err);
      setLoaded(true);
    });
  }, [user, dataReady]);

  const daysPlayed = userResults.length;
  const resultMap = {};
  userResults.forEach(r => { resultMap[r.day] = Number(r.score); });

  // Month pages for the archive — one calendar month per page, newest last.
  const monthPages = [];
  for (let d = 1; d <= ALL_PUZZLES.length; d++) {
    const cal = getPuzzleDayDate(d);
    const key = cal.getFullYear() * 12 + cal.getMonth();
    const last = monthPages[monthPages.length - 1];
    if (!last || last.key !== key) monthPages.push({ key, year: cal.getFullYear(), month: cal.getMonth(), days: [d] });
    else last.days.push(d);
  }
  const monthIdx = archiveMonthIdx === null ? monthPages.length - 1 : archiveMonthIdx;
  const monthPage = monthPages[monthIdx];

  if (subview === "archive") {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans', sans-serif", padding: "28px 16px" }}>
        <div style={{ maxWidth: 460, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
            <button onClick={() => setSubview("stats")} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.muted, background: "none", border: "none", cursor: "pointer", padding: 0 }}>← Back</button>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: C.accent }}>Archive</span>
          </div>

          {!loaded ? (
            <div style={{ textAlign: "center", color: C.muted, fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 2, padding: "24px 0" }}>LOADING...</div>
          ) : (
          <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button
              onClick={() => setArchiveMonthIdx(Math.max(0, monthIdx - 1))}
              disabled={monthIdx === 0}
              style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: monthIdx === 0 ? "#333" : C.muted, background: "none", border: "none", cursor: monthIdx === 0 ? "default" : "pointer", padding: "4px 10px" }}>
              ‹
            </button>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: C.text }}>
              {MONTH_SHORT[monthPage.month]} {monthPage.year}
            </span>
            <button
              onClick={() => setArchiveMonthIdx(Math.min(monthPages.length - 1, monthIdx + 1))}
              disabled={monthIdx === monthPages.length - 1}
              style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: monthIdx === monthPages.length - 1 ? "#333" : C.muted, background: "none", border: "none", cursor: monthIdx === monthPages.length - 1 ? "default" : "pointer", padding: "4px 10px" }}>
              ›
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8 }}>
            {monthPage.days.map(day => {
              const isToday = day === todayPuzzleDay;
              const calDate = getPuzzleDayDate(day);
              const isFuture = calDate > new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
              const score = resultMap[day];
              const isPlayed = day in resultMap;
              const bgColor = isFuture ? "#141414" : "#1e1e1e";
              const calDay = calDate.getDate();
              const calMonth = MONTH_SHORT[calDate.getMonth()];
              const textColor = isFuture ? "#333" : C.muted;
              const isClickable = !isFuture && !isToday && !!user;
              return (
                <div key={day}
                  onClick={isClickable ? () => onSelectDay(day) : undefined}
                  style={{
                    background: bgColor,
                    border: `2px solid ${isToday ? C.accent : "transparent"}`,
                    borderRadius: 6,
                    padding: "8px 4px 6px",
                    textAlign: "center",
                    cursor: isClickable ? "pointer" : "default",
                  }}>
                  <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 17, fontWeight: 600, color: textColor, lineHeight: 1 }}>{calDay}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 7, color: textColor, letterSpacing: 1, marginTop: 1 }}>{calMonth}</div>
                  {isPlayed && score > 0 && (
                    <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 13, fontWeight: 700, color: C.accent, lineHeight: 1, marginTop: 3 }}>{score}</div>
                  )}
                  {isPlayed && score === 0 && (
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, color: C.muted, lineHeight: 1, marginTop: 3 }}>✕</div>
                  )}
                </div>
              );
            })}
          </div>
          </>
          )}
        </div>
      </div>
    );
  }

  let greenCount = 0, yellowCount = 0, redCount = 0;
  const scoreCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
  userResults.forEach(r => {
    (r.round_results || []).forEach(o => {
      if (o === "perfect") greenCount++;
      else if (o === "second") yellowCount++;
      else redCount++;
    });
    scoreCounts[Number(r.score)] = (scoreCounts[Number(r.score)] || 0) + 1;
  });
  const totalRounds = greenCount + yellowCount + redCount;
  const totalGames = userResults.length;
  const pct = n => totalRounds ? Math.round(n / totalRounds * 100) : 0;
  const maxBucket = Math.max(...Object.values(scoreCounts), 1);
  const STAT_GREEN = "#639922", STAT_YELLOW = "#EF9F27", STAT_RED = "#E24B4A";
  const guessCards = [
    { color: STAT_GREEN,  count: greenCount,  label: "First guess",  pct: pct(greenCount)  },
    { color: STAT_YELLOW, count: yellowCount, label: "Second guess", pct: pct(yellowCount) },
    { color: STAT_RED,    count: redCount,    label: "Missed",       pct: pct(redCount)    },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans', sans-serif", padding: "28px 16px" }}>
      <div style={{ maxWidth: 460, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
          <button onClick={onBack} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.muted, background: "none", border: "none", cursor: "pointer", padding: 0 }}>← Back</button>
          <div style={{ flex: 1, height: 1, background: C.border }} />
          <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: C.accent }}>Profile</span>
        </div>

        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 20, textTransform: "uppercase", textAlign: "center" }}>
          {user?.email}
        </div>

        <StatsBar streakData={streakData} daysPlayed={daysPlayed} />

        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, color: C.muted, textTransform: "uppercase", marginBottom: 12 }}>Guess breakdown</div>
        <div style={{ display: "flex", gap: 10, marginBottom: 32 }}>
          {guessCards.map(({ color, count, label, pct }) => (
            <div key={label} style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 8px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: color }} />
              <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 32, fontWeight: 700, color: C.text, lineHeight: 1 }}>{count}</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.muted, lineHeight: 1.3 }}>{label}</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: color }}>{pct}%</div>
            </div>
          ))}
        </div>

        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Score distribution</div>
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.muted, marginBottom: 16 }}>
          {totalGames} {totalGames === 1 ? "game" : "games"} played
        </div>
        {totalGames === 0 ? (
          <div style={{ textAlign: "center", color: C.muted, fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 2, padding: "24px 0" }}>No games played yet</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[4, 3, 2, 1, 0].map(s => {
              const count = scoreCounts[s];
              const barWidth = animated ? Math.round(count / maxBucket * 100) : 0;
              return (
                <div key={s} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: C.text, width: 16, textAlign: "center", flexShrink: 0 }}>{s}</div>
                  <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, height: 28, overflow: "hidden" }}>
                    <div style={{ width: `${barWidth}%`, height: "100%", background: STAT_GREEN, borderRadius: 4, transition: "width 0.6s ease" }} />
                  </div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: C.muted, width: 24, textAlign: "right", flexShrink: 0 }}>{count}</div>
                </div>
              );
            })}
          </div>
        )}

        <button onClick={() => setSubview("archive")} style={{ width: "100%", marginTop: 32, marginBottom: 20, background: "none", color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "13px", fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace" }}>
          Archive →
        </button>

        {user ? (
          <>
            <button onClick={handleSignOut} style={{ marginTop: 32, width: "100%", background: "transparent", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: "13px", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace" }}>
              Sign Out
            </button>
            {signOutErr && <div style={{ marginTop: 10, textAlign: "center", fontSize: 12, color: C.red, fontFamily: "'DM Sans', sans-serif" }}>{signOutErr}</div>}
          </>) : (
          <div style={{ marginTop: 32 }}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.muted, marginBottom: 14, textAlign: "center" }}>Sign in to save your streak across devices.</div>
            <button
              onClick={onGoogleLogin}
              style={{ width: "100%", background: "#fff", color: "#111", border: "none", borderRadius: 6, padding: "14px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#4285F4" d="M44.5 20H24v8.5h11.7C34.2 33.6 29.6 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 6 1.1 8.2 3l6-6C34.5 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.9 0 20-7.9 20-21 0-1.3-.1-2.7-.5-4z"/><path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.1 13 24 13c3.1 0 6 1.1 8.2 3l6-6C34.5 5.1 29.5 3 24 3 16.1 3 9.3 7.9 6.3 14.7z"/><path fill="#FBBC05" d="M24 45c5.4 0 10.3-1.8 14.1-4.9l-6.5-5.3C29.6 36.5 27 37 24 37c-5.6 0-10.3-3.4-12.2-8.3l-7 5.4C8.1 40.5 15.5 45 24 45z"/><path fill="#EA4335" d="M44.5 20H24v8.5h11.7c-.8 2.4-2.3 4.4-4.2 5.8l6.5 5.3C41.8 36.1 45 30.5 45 24c0-1.3-.2-2.7-.5-4z"/></svg>
              Continue with Google
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RoundReviewScreen({ puzzle, dayNum, roundResults, onBack, backLabel = "Back to Results" }) {
  const [roundIdx, setRoundIdx] = useState(0);
  const touchStartX = useRef(null);
  const round = puzzle.rounds[roundIdx];
  const result = roundResults[roundIdx] ?? "wrong";

  const bgTint = result === "perfect" ? "rgba(62,200,122,0.20)"
    : result === "second" ? "rgba(212,168,50,0.20)"
    : "rgba(232,80,80,0.20)";

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx < -50 && roundIdx < TOTAL_ROUNDS - 1) setRoundIdx(r => r + 1);
    if (dx > 50 && roundIdx > 0) setRoundIdx(r => r - 1);
  };

  const resultLabel = result === "perfect" ? "First go 🟩"
    : result === "second" ? "Second try 🟨"
    : "Missed this one 🟥";

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "28px 20px" }}>
      <div style={{ maxWidth: 420, width: "100%" }}>

        <div style={{ marginBottom: 20 }}>
          <button onClick={onBack} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.muted, background: "none", border: "none", cursor: "pointer", padding: 0 }}>← {backLabel}</button>
        </div>

        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16, textAlign: "center" }}>
          Day {dayNum} · Round {roundIdx + 1} of {TOTAL_ROUNDS}
        </div>

        <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} style={{ background: bgTint, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 16px 24px", position: "relative" }}>
          <div style={{ position: "absolute", top: 14, right: 16, fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 1, color: C.muted, textTransform: "uppercase" }}>
            {resultLabel}
          </div>

          <div style={{ marginBottom: 18, paddingRight: 110 }}>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: C.text, lineHeight: 1.2 }}>
              {round.question}
            </div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.accent, marginTop: 5 }}>
              {round.hint}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {round.items.map((item, i) => (
              <div key={item.label} style={{ display: "flex", alignItems: "center", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px", gap: 12 }}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: C.muted, minWidth: 16 }}>{i + 1}</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600, color: C.text, flex: 1 }}>{item.label}</span>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: C.accent }}>{item.display}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 24 }}>
          <button
            onClick={() => setRoundIdx(r => r - 1)}
            disabled={roundIdx === 0}
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: roundIdx === 0 ? C.border : C.muted, background: "none", border: "none", cursor: roundIdx === 0 ? "default" : "pointer", padding: 0 }}
          >← Prev</button>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {puzzle.rounds.map((_, i) => {
              const r = roundResults[i] ?? "wrong";
              return <div key={i} style={{ width: roundIdx === i ? 18 : 8, height: 8, borderRadius: 4, background: r === "perfect" ? C.green : r === "second" ? C.yellow : C.red, transition: "width 0.2s", opacity: roundIdx === i ? 1 : 0.5 }} />;
            })}
          </div>

          <button
            onClick={() => setRoundIdx(r => r + 1)}
            disabled={roundIdx === TOTAL_ROUNDS - 1}
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: roundIdx === TOTAL_ROUNDS - 1 ? C.border : C.muted, background: "none", border: "none", cursor: roundIdx === TOTAL_ROUNDS - 1 ? "default" : "pointer", padding: 0 }}
          >Next →</button>
        </div>

      </div>
    </div>
  );
}

// Celebration audio — synthesised (no asset files) so it works offline and under CSP.
// A loud fanfare/noise via Web Audio + a spoken line via SpeechSynthesis.
function playCelebSound(mode) {
  try {
    const ACtx = window.AudioContext || window.webkitAudioContext;
    if (ACtx) {
      const ac = new ACtx();
      if (ac.state === "suspended") { try { ac.resume(); } catch (e) {} }
      const t0 = ac.currentTime;
      const blip = (freq, start, dur, type, vol) => {
        const o = ac.createOscillator(), g = ac.createGain();
        o.type = type;
        o.frequency.setValueAtTime(freq, t0 + start);
        g.gain.setValueAtTime(0.0001, t0 + start);
        g.gain.exponentialRampToValueAtTime(vol, t0 + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
        o.connect(g).connect(ac.destination);
        o.start(t0 + start); o.stop(t0 + start + dur + 0.03);
      };
      if (mode === "banana") {
        // Goofy wobbly slide-whistle boings + a cheeky honk
        for (let i = 0; i < 5; i++) {
          const o = ac.createOscillator(), g = ac.createGain();
          o.type = "sine";
          const s = t0 + i * 0.16;
          o.frequency.setValueAtTime(180 + Math.random() * 260, s);
          o.frequency.exponentialRampToValueAtTime(700 + Math.random() * 400, s + 0.14);
          o.frequency.exponentialRampToValueAtTime(170, s + 0.3);
          g.gain.setValueAtTime(0.0001, s);
          g.gain.exponentialRampToValueAtTime(0.32, s + 0.03);
          g.gain.exponentialRampToValueAtTime(0.0001, s + 0.32);
          o.connect(g).connect(ac.destination);
          o.start(s); o.stop(s + 0.34);
        }
        blip(880, 0.85, 0.45, "square", 0.22);
        blip(587, 1.05, 0.5, "square", 0.2);
      } else {
        // Triumphant ascending fanfare (C-E-G-C) doubled, plus an airhorn hold
        const notes = [523.25, 659.25, 783.99, 1046.5];
        notes.forEach((f, i) => blip(f, i * 0.12, 0.5, "triangle", 0.38));
        notes.forEach((f, i) => blip(f, 0.55 + i * 0.09, 0.6, "square", 0.24));
        blip(1046.5, 1.0, 0.85, "sawtooth", 0.28);
      }
    }
  } catch (e) {}
  try {
    if (window.speechSynthesis) {
      const line = mode === "banana"
        ? "You won a banana!"
        : "You are the winner, Congratulations! No Peckning";
      const u = new SpeechSynthesisUtterance(line);
      u.rate = mode === "banana" ? 1.15 : 0.95;
      u.pitch = mode === "banana" ? 1.5 : 1.0;
      u.volume = 1;
      window.speechSynthesis.cancel();
      // let the fanfare land first, then the voice
      setTimeout(() => {
        try { window.speechSynthesis.speak(u); } catch (e) {}
        if (mode === "banana") {
          // then chant it home 3x (audio only — deliberately not shown on screen)
          const chant = new SpeechSynthesisUtterance(
            "Chicken, banana, chicken, banana, chicken, banana, banana, banana. ".repeat(3)
          );
          chant.rate = 1.2; chant.pitch = 1.5; chant.volume = 1;
          try { window.speechSynthesis.speak(chant); } catch (e) {}
        }
      }, mode === "banana" ? 250 : 550);
    }
  } catch (e) {}
}

function CompleteScreen({ roundResults, puzzle, onCopy, shareState, user, streakData, userResults, onGoogleLogin, onProfile, onPrivacy, onReview }) {
  const score = roundResults.filter(r => r !== "wrong").length;
  const allGreen = score === TOTAL_ROUNDS && roundResults.every(r => r === "perfect");
  const allYellow = score === TOTAL_ROUNDS && roundResults.every(r => r === "second");
  const celebMode = allGreen ? "perfect" : allYellow ? "banana" : null;
  const streak = streakData?.current_streak ?? 0;
  const nickname = NICKNAMES[Math.floor(Math.random() * NICKNAMES.length)];
  const daysPlayed = userResults.length;

  const [quip, setQuip] = useState(null);
  const [quipLoading, setQuipLoading] = useState(true);
  const [shaking, setShaking] = useState(false);
  const [celebActive, setCelebActive] = useState(false);
  const [slamVisible, setSlamVisible] = useState(false);
  const [squaresCount, setSquaresCount] = useState(0);
  const [quipVisible, setQuipVisible] = useState(false);
  const canvasRef = useRef(null);
  const bgOverlayRef = useRef(null);
  const rafRef = useRef(null);
  const cleanupFns = useRef([]);

  // Inject celebration CSS once
  useEffect(() => {
    if (document.getElementById("po-celeb-style")) return;
    const s = document.createElement("style");
    s.id = "po-celeb-style";
    s.textContent = `
      @keyframes screenShake {
        0%,100%{transform:translate(0,0)}
        10%{transform:translate(-6px,3px)}
        20%{transform:translate(5px,-4px)}
        30%{transform:translate(-3px,6px)}
        40%{transform:translate(4px,-5px)}
        50%{transform:translate(-5px,2px)}
        60%{transform:translate(6px,3px)}
        70%{transform:translate(-4px,-3px)}
        80%{transform:translate(3px,5px)}
        90%{transform:translate(-5px,-4px)}
      }
      @keyframes slamIn {
        0%{transform:scale(4);opacity:0}
        60%{transform:scale(0.9);opacity:1}
        80%{transform:scale(1.05)}
        100%{transform:scale(1)}
      }
      @keyframes pulseGlow {
        0%,100%{text-shadow:0 0 20px #D4A832,0 0 40px #D4A832}
        50%{text-shadow:0 0 40px #D4A832,0 0 80px #D4A832,0 0 120px #D4A832}
      }
      @keyframes fadeUpQuip {
        0%{transform:translateY(20px);opacity:0}
        100%{transform:translateY(0);opacity:1}
      }
      @keyframes squarePop {
        0%{transform:scale(0);opacity:0}
        70%{transform:scale(1.15);opacity:1}
        100%{transform:scale(1)}
      }
      @keyframes bananaFlash {
        0%{transform:scale(1) rotate(-4deg);color:#F0C040;text-shadow:0 0 18px #F0C040}
        25%{transform:scale(1.28) rotate(5deg);color:#FFE23A;text-shadow:0 0 55px #FFE23A}
        50%{transform:scale(0.94) rotate(-7deg);color:#FFFFFF;text-shadow:0 0 40px #F0C040}
        75%{transform:scale(1.22) rotate(6deg);color:#FFD400;text-shadow:0 0 60px #FFD400}
        100%{transform:scale(1) rotate(-4deg);color:#F0C040;text-shadow:0 0 18px #F0C040}
      }
    `;
    document.head.appendChild(s);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch AI quip — abort after 5s so a cold-start never hangs the UI
  useEffect(() => {
    const RESULT_WORD = { perfect: "nailed", second: "scraped", wrong: "dropped" };
    const roundSummary = puzzle.rounds
      .map((r, i) => `R${i + 1} (${r.question}): ${RESULT_WORD[roundResults[i]]}`)
      .join("\n");

    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    fetch("/.netlify/functions/flavour-text", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ score: `${score}/${TOTAL_ROUNDS}`, allGreen, allYellow, streak, nickname, roundSummary }),
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        // The function returns 200 with a canned line when the API call fails, so
        // a degraded quip is otherwise invisible. Report it rather than guess.
        if (d.fallback && window.umami) umami.track('quip-fallback', { reason: 'api' });
        setQuip(d.quip);
        setQuipLoading(false);
      })
      .catch(() => {
        // Network failure or the 5s abort above. The cancelled guard means an
        // unmount-triggered abort is not counted as a failure.
        if (cancelled) return;
        if (window.umami) umami.track('quip-fallback', { reason: 'timeout' });
        setQuipLoading(false);
      });
    return () => { cancelled = true; clearTimeout(timeout); controller.abort(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Perfect game (4 green) AND banana (4 yellow) celebration — direct DOM manipulation
  useEffect(() => {
    if (!celebMode) return;
    const isBanana = celebMode === "banana";

    const EMOJIS = isBanana
      ? ["🍌","🍌","🍌","🍌","🐵","🐒","🙈","🙉","🙊","🍌"]
      : ["🐔","🐣","🐤","🐥","🏆","🥚","🎉","🎊","👑","🪺","🐓","🍗"];
    const RAINBOW = isBanana
      ? ["#F0C040","#FFE23A","#FFD400","#000000","#F5A623","#FFF176","#000000"]
      : ["#FF0000","#FF4400","#FF8800","#FFCC00","#FFFF00","#88FF00","#00FF00","#00FF88","#00FFFF","#0088FF","#0000FF","#4400FF","#8800FF","#CC00FF","#FF00FF","#FF0088"];
    const chickens = [];
    let raveMode = false;
    let rainbowIdx = 0;
    let frameCount = 0;

    const addTimeout = (fn, ms) => { const id = setTimeout(fn, ms); cleanupFns.current.push(() => clearTimeout(id)); };
    const addInterval = (fn, ms) => { const id = setInterval(fn, ms); cleanupFns.current.push(() => clearInterval(id)); };

    const overlay = bgOverlayRef.current;
    const canvas = canvasRef.current;

    const spawnChicken = () => {
      if (!canvas) return;
      const size = 18 + Math.random() * 30;
      const edge = Math.floor(Math.random() * 4);
      const speed = 1.5 + Math.random() * 2;
      let x, y, vx, vy;
      if      (edge === 0) { x = Math.random() * canvas.width;  y = -size;               vx = (Math.random()-0.5)*2; vy = speed; }
      else if (edge === 1) { x = canvas.width + size;            y = Math.random() * canvas.height; vx = -speed; vy = (Math.random()-0.5)*2; }
      else if (edge === 2) { x = Math.random() * canvas.width;  y = canvas.height + size; vx = (Math.random()-0.5)*2; vy = -speed; }
      else                 { x = -size;                          y = Math.random() * canvas.height; vx = speed;  vy = (Math.random()-0.5)*2; }
      chickens.push({ x, y, vx, vy, size, rotation: Math.random()*Math.PI*2, rotSpeed: (Math.random()-0.5)*0.1, emoji: EMOJIS[Math.floor(Math.random()*EMOJIS.length)] });
    };

    const drawLoop = () => {
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      frameCount++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (raveMode) {
        ctx.fillStyle = `rgba(0,0,0,${0.25 + 0.15 * Math.sin(frameCount * 0.1)})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      for (const c of chickens) {
        c.x += c.vx; c.y += c.vy; c.rotation += c.rotSpeed;
        if (raveMode) {
          if (c.x < 0 || c.x > canvas.width)  { c.vx *= -1; c.x = Math.max(0, Math.min(canvas.width,  c.x)); }
          if (c.y < 0 || c.y > canvas.height) { c.vy *= -1; c.y = Math.max(0, Math.min(canvas.height, c.y)); }
        }
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.rotation);
        ctx.font = `${c.size}px serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(c.emoji, 0, 0);
        ctx.restore();
      }
      rafRef.current = requestAnimationFrame(drawLoop);
    };

    const sizeCanvas = () => { if (canvas) { canvas.width = window.innerWidth; canvas.height = window.innerHeight; } };
    window.addEventListener("resize", sizeCanvas);
    cleanupFns.current.push(() => window.removeEventListener("resize", sizeCanvas));

    // Kick off
    sizeCanvas();
    canvas.style.display = "block";
    overlay.style.display = "block";
    overlay.style.background = "rgba(0,0,0,0.95)";
    setCelebActive(true);

    // t=0.9s — flash, shake, SOUND, spawn first 35 emojis
    addTimeout(() => {
      overlay.style.transition = "none";
      overlay.style.background = isBanana ? "#F0C040" : "#F5A623";
      setShaking(true);
      playCelebSound(celebMode);
      drawLoop();
      for (let i = 0; i < 35; i++) addTimeout(spawnChicken, i * 50);
      addTimeout(() => { overlay.style.background = "rgba(0,0,0,0.85)"; }, 120);
    }, 900);

    // t=2.9s — rave: 25 more chickens, rainbow bg, score slam
    addTimeout(() => {
      raveMode = true;
      chickens.forEach(c => { c.vx *= 1.4; c.vy *= 1.4; c.rotSpeed *= 2; });
      for (let i = 0; i < 25; i++) addTimeout(spawnChicken, i * 50);
      addInterval(() => { if (overlay) { overlay.style.background = RAINBOW[rainbowIdx++ % RAINBOW.length] + "72"; } }, 60);
      setSlamVisible(true);
      for (let i = 0; i < 4; i++) addTimeout(() => setSquaresCount(n => n + 1), i * 120);
    }, 2900);

    // t=3.8s — quip fades up
    addTimeout(() => setQuipVisible(true), 3800);

    // t=8.9s — settle: slow chickens, fade bg to dark
    addTimeout(() => {
      raveMode = false;
      chickens.forEach(c => { c.vx *= 0.3; c.vy *= 0.3; c.rotSpeed *= 0.3; });
      overlay.style.transition = "background 1s ease-out";
      overlay.style.background = "transparent";
    }, 8900);

    // t=15.9s — done: stop shake, hide overlay/canvas, show normal content
    addTimeout(() => {
      setShaking(false);
      setCelebActive(false);
      setSlamVisible(false);
      overlay.style.display = "none";
      canvas.style.display = "none";
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    }, 15900);

    return () => {
      cleanupFns.current.forEach(fn => fn());
      cleanupFns.current = [];
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "28px 20px", animation: shaking ? "screenShake 0.15s ease infinite" : "none" }}>

      {/* Celebration overlay + canvas — in DOM for perfect (4 green) and banana (4 yellow) games so refs are ready */}
      {celebMode && (
        <>
          <div ref={bgOverlayRef} style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, zIndex: 100, pointerEvents: "none", display: "none" }} />
          <canvas ref={canvasRef} style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0, zIndex: 101, pointerEvents: "none", display: "none" }} />
          {slamVisible && (
            <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 102, textAlign: "center", width: "100%", padding: "0 20px", boxSizing: "border-box" }}>
              {celebMode === "banana" ? (
                <>
                  <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 58, fontWeight: 700, lineHeight: 1.05, animation: "bananaFlash 0.35s linear infinite" }}>
                    YOU WON A BANANA! 🍌
                  </div>
                  <div style={{ fontSize: 44, marginTop: 6, animation: "screenShake 0.18s ease infinite" }}>🐵 🍌 🙈 🍌 🐒</div>
                </>
              ) : (
                <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 80, fontWeight: 700, color: C.text, lineHeight: 1, animation: "slamIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards, pulseGlow 1.5s ease-in-out infinite 0.5s" }}>
                  {score}<span style={{ fontSize: 38, color: C.muted, fontWeight: 600 }}>/{TOTAL_ROUNDS}</span>
                </div>
              )}
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 12 }}>
                {[0,1,2,3].map(i => squaresCount > i ? (
                  <div key={i} style={{ width: 48, height: 48, borderRadius: 8, background: celebMode === "banana" ? C.yellow : C.green, animation: "squarePop 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards" }} />
                ) : null)}
              </div>
              {quipVisible && quip && (
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: C.text, lineHeight: 1.5, animation: "fadeUpQuip 0.5s ease forwards", maxWidth: 340, margin: "20px auto 0" }}>
                  {quip}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Normal complete screen content */}
      <div style={{ maxWidth: 420, width: "100%", textAlign: "center", opacity: celebActive ? 0 : 1, pointerEvents: celebActive ? "none" : "auto", transition: "opacity 0.5s" }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 44, fontWeight: 700, color: C.accent, letterSpacing: "-1px" }}>PECKING ORDER</div>
        </div>

        <div style={{ display: "flex", gap: 6, justifyContent: "center", margin: "14px 0 46px" }}>
          {Array(TOTAL_ROUNDS).fill(0).map((_, i) => {
            const r = roundResults[i];
            return <div key={i} style={{ width: 30, height: 5, borderRadius: 3, background: r === "perfect" ? C.green : r === "second" ? C.yellow : C.red }} />;
          })}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 10 }}>
          {roundResults.map((r, i) => (
            <div key={i} style={{ width: 48, height: 48, borderRadius: 8, background: r === "perfect" ? C.green : r === "second" ? C.yellow : C.red }} />
          ))}
        </div>

        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 80, fontWeight: 700, color: C.text, lineHeight: 1 }}>
          {score}<span style={{ fontSize: 38, color: C.muted, fontWeight: 600 }}>/{TOTAL_ROUNDS}</span>
        </div>
        {/* AI quip replaces static message */}
        <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, color: C.text, marginBottom: 28, marginTop: 12, lineHeight: 1.5, padding: "0 12px", minHeight: 28 }}>
          {quipLoading ? "…" : (quip || "")}
        </div>

        {/* Auth section */}
        {user ? (
          <StatsBar streakData={streakData} daysPlayed={daysPlayed} />
        ) : (
          <div style={{ marginBottom: 20, padding: "18px 16px", background: C.surface, border: `1px solid ${C.accent}`, borderRadius: 8, textAlign: "center" }}>
            <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 20, fontWeight: 700, color: C.accent, marginBottom: 4 }}>Save your streak & challenge mates</div>
            <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.muted, marginBottom: 14 }}>Free · no password · one tap</div>
            <button
              onClick={onGoogleLogin}
              style={{ width: "100%", background: "#fff", color: "#111", border: "none", borderRadius: 6, padding: "12px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, transition: "opacity 0.2s" }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#4285F4" d="M44.5 20H24v8.5h11.7C34.2 33.6 29.6 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 6 1.1 8.2 3l6-6C34.5 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.9 0 20-7.9 20-21 0-1.3-.1-2.7-.5-4z"/><path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.1 13 24 13c3.1 0 6 1.1 8.2 3l6-6C34.5 5.1 29.5 3 24 3 16.1 3 9.3 7.9 6.3 14.7z"/><path fill="#FBBC05" d="M24 45c5.4 0 10.3-1.8 14.1-4.9l-6.5-5.3C29.6 36.5 27 37 24 37c-5.6 0-10.3-3.4-12.2-8.3l-7 5.4C8.1 40.5 15.5 45 24 45z"/><path fill="#EA4335" d="M44.5 20H24v8.5h11.7c-.8 2.4-2.3 4.4-4.2 5.8l6.5 5.3C41.8 36.1 45 30.5 45 24c0-1.3-.2-2.7-.5-4z"/></svg>
              Continue with Google
            </button>
          </div>
        )}

        <button onClick={() => onCopy(quip)} style={{ width: "100%", background: shareState === "shared" || shareState === "copied" ? C.green : C.accent, color: "#000", border: "none", borderRadius: 6, padding: "17px", fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace", transition: "background 0.25s" }}>
          {shareState === "copied" ? "✓  Copied to clipboard" : shareState === "shared" ? "✓  Shared" : shareState === "failed" ? "Couldn't share — try again" : "Share Result"}
        </button>

        <button onClick={onReview} style={{ width: "100%", marginTop: 10, background: "none", color: C.text, border: `1px solid ${C.muted}`, borderRadius: 6, padding: "13px", fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace" }}>
          Review Rounds
        </button>

        {user && (
          <button onClick={onProfile} style={{ width: "100%", marginTop: 10, background: "none", color: C.text, border: `1px solid ${C.muted}`, borderRadius: 6, padding: "13px", fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace" }}>
            Profile
          </button>
        )}

        <div style={{ marginTop: 14, textAlign: "center" }}>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 28, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: 1 }}>
            New Puzzle Tomorrow!
          </div>
        </div>
      </div>
    </div>
  );
}

function timeUntilMidnight() {
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const diff = midnight - now;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

function LockedScreen({ puzzle, userResults, todayPuzzleDay, user, streakData, onProfile, onSignOut }) {
  const [timeLeft, setTimeLeft] = useState(timeUntilMidnight());
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(timeUntilMidnight()), 1000);
    return () => clearInterval(id);
  }, []);
  const todayResult = userResults.find(r => r.day === todayPuzzleDay);
  const roundResults = todayResult?.round_results ?? [];
  const score = roundResults.filter(r => r !== "wrong").length;
  const daysPlayed = userResults.length;
  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "28px 20px" }}>
      <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 8 }}>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 36, fontWeight: 700, color: C.accent, letterSpacing: "-0.5px" }}>PECKING ORDER</div>
          {user && (
            <button onClick={onProfile} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", flexShrink: 0 }}>
              <ProfileIcon color={C.accent} />
            </button>
          )}
        </div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 2, color: C.muted, textTransform: "uppercase", marginBottom: 32 }}>
          Day {todayPuzzleDay} · {puzzle.label}
        </div>
        {roundResults.length > 0 && (
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 16 }}>
            {roundResults.map((r, i) => (
              <div key={i} style={{ width: 48, height: 48, borderRadius: 8, background: r === "perfect" ? C.green : r === "second" ? C.yellow : C.red }} />
            ))}
          </div>
        )}
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 72, fontWeight: 700, color: C.text, lineHeight: 1, marginBottom: 32 }}>
          {score}<span style={{ fontSize: 32, color: C.muted, fontWeight: 600 }}>/{TOTAL_ROUNDS}</span>
        </div>
        <StatsBar streakData={streakData} daysPlayed={daysPlayed} />
        <div style={{ padding: "20px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 20 }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>Next puzzle in</div>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 36, fontWeight: 700, color: C.accent }}>{timeLeft}</div>
        </div>
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 22, fontWeight: 700, color: C.text }}>Come back tomorrow!</div>
      </div>
    </div>
  );
}

function ArchiveResultsView({ puzzle, dayNum, roundResults, onBack }) {
  const [shareState, setShareState] = useState("idle");
  const [reviewing, setReviewing] = useState(false);
  const score = roundResults.filter(r => r !== "wrong").length;

  if (reviewing) return (
    <RoundReviewScreen
      puzzle={puzzle} dayNum={dayNum} roundResults={roundResults}
      onBack={() => setReviewing(false)} backLabel="Back to Results"
    />
  );
  const calDate = getPuzzleDayDate(dayNum);
  const dayLabel = `Day ${dayNum} · ${calDate.getDate()} ${MONTH_SHORT[calDate.getMonth()]}`;

  const handleCopy = async () => {
    const outcome = await shareText(buildShareText({ day: dayNum, roundResults }));
    if (outcome === "cancelled") return;
    setShareState(outcome);
    setTimeout(() => setShareState("idle"), 2500);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "28px 20px" }}>
      <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
        <div style={{ textAlign: "left", marginBottom: 20 }}>
          <button onClick={onBack} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.muted, background: "none", border: "none", cursor: "pointer", padding: 0 }}>← Back to Archive</button>
        </div>
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 44, fontWeight: 700, color: C.accent, letterSpacing: "-1px", marginBottom: 6 }}>PECKING ORDER</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: C.muted, letterSpacing: 2, textTransform: "uppercase", marginBottom: 28 }}>{dayLabel}</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 16 }}>
          {roundResults.map((r, i) => (
            <div key={i} style={{ width: 48, height: 48, borderRadius: 8, background: r === "perfect" ? C.green : r === "second" ? C.yellow : C.red }} />
          ))}
        </div>
        <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 80, fontWeight: 700, color: C.text, lineHeight: 1, marginBottom: 28 }}>
          {score}<span style={{ fontSize: 38, color: C.muted, fontWeight: 600 }}>/{TOTAL_ROUNDS}</span>
        </div>
        <button onClick={handleCopy} style={{ width: "100%", background: shareState === "shared" || shareState === "copied" ? C.green : C.accent, color: "#000", border: "none", borderRadius: 6, padding: "17px", fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace", transition: "background 0.25s" }}>
          {shareState === "copied" ? "✓  Copied to clipboard" : shareState === "shared" ? "✓  Shared" : shareState === "failed" ? "Couldn't share — try again" : "Share Result"}
        </button>
        <button onClick={() => setReviewing(true)} style={{ width: "100%", marginTop: 10, background: "none", color: C.text, border: `1px solid ${C.muted}`, borderRadius: 6, padding: "13px", fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace" }}>
          Review Rounds
        </button>
      </div>
    </div>
  );
}

// Card geometry. Every card in a round is given one height, measured from the
// tallest label in that round, so an answer that wraps to a second line keeps
// its padding even and never crowds the card below it. CARD_MIN_H + ROW_GAP
// reproduce the single-line spacing the game had before cards could grow.
const CARD_MIN_H = 52;
const CARD_PAD_Y = 16;
const ROW_GAP = 18;

// Shared game engine: owns the 4-round loop, drag/touch reorder, two-guess flow,
// FLIP reveal animation, and the in-play screen JSX. Used by both the main daily
// game (OrderUp) and the archive replay (ArchiveGameScreen). Screen-specific
// behaviour comes in via props:
//   renderHeader(pips) — screen header; receives the progress-pips element to slot in
//   footer             — optional node rendered under the CTA area
//   onStarted()        — fired once on the first submit (main game gates replay-restore on it)
//   onGameComplete(roundResults) — fired when "See Results" is tapped after round 4
function GameEngine({ puzzle, renderHeader, footer, onStarted, onGameComplete }) {
  const [round, setRound] = useState(0);
  const [phase, setPhase] = useState("ordering");
  const [order, setOrder] = useState(() => shuffle(puzzle.rounds[0].items));
  const [guess1Count, setGuess1Count] = useState(null);
  const [guess1Correct, setGuess1Correct] = useState([]);
  const [guess1Order, setGuess1Order] = useState(null);
  const [dupGuessMsg, setDupGuessMsg] = useState(false);
  const [dragSrcLabel, setDragSrcLabel] = useState(null);
  const [lastGuess, setLastGuess] = useState(null);
  const [roundResults, setRoundResults] = useState([]);
  const [revealedCards, setRevealedCards] = useState([]);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [flipDone, setFlipDone] = useState(false);
  const [rowH, setRowH] = useState(64);

  const dragFrom = useRef(null);
  const itemRefs = useRef([]);
  const snapPositions = useRef({});
  const cardRefMap = useRef({});
  const labelRefs = useRef([]);
  const startedRef = useRef(false);

  const currentRound = puzzle.rounds[round];
  const correctOrder = currentRound.items;
  const countCorrect = (arr) => arr.filter((item, i) => item.label === correctOrder[i].label).length;

  const reorderUnlocked = (prev, from, to) => {
    if (from === to) return prev;
    if (guess1Correct.includes(prev[from]?.label)) return prev;
    const unlockedIdx = prev.map((_, idx) => idx).filter(idx => !guess1Correct.includes(prev[idx]?.label));
    const unlockedCards = unlockedIdx.map(idx => prev[idx]);
    const fromU = unlockedIdx.indexOf(from);
    let toU = -1, minDist = Infinity;
    unlockedIdx.forEach((slotIdx, j) => { const dist = Math.abs(slotIdx - to); if (dist < minDist) { minDist = dist; toU = j; } });
    if (fromU === -1 || toU === -1 || fromU === toU) return prev;
    const newUnlocked = [...unlockedCards];
    const [card] = newUnlocked.splice(fromU, 1);
    newUnlocked.splice(toU, 0, card);
    const result = [...prev];
    unlockedIdx.forEach((slotIdx, j) => { result[slotIdx] = newUnlocked[j]; });
    return result;
  };

  // Every card in the round is as tall as the tallest label needs, so a wrapped
  // answer keeps even padding instead of spilling into the card below.
  const measuredRowH = () => {
    let tallest = 0;
    labelRefs.current.forEach(el => { if (el) tallest = Math.max(tallest, el.offsetHeight); });
    return tallest > 0 ? Math.max(CARD_MIN_H, Math.ceil(tallest) + CARD_PAD_Y * 2) : 0;
  };

  // A new round measures from scratch. Resize and webfont load both re-wrap the
  // labels, so both re-measure too.
  useLayoutEffect(() => {
    const measure = () => { const h = measuredRowH(); if (h) setRowH(h); };
    measure();
    window.addEventListener("resize", measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
    return () => window.removeEventListener("resize", measure);
  }, [round]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revealing a value swaps the narrow drag handle for the number, which can push
  // a borderline label onto another line. Within a round the row only ever grows,
  // so that never clips a card and never shuffles the layout mid-reveal.
  useLayoutEffect(() => {
    const h = measuredRowH();
    if (h) setRowH(prev => Math.max(prev, h));
  }, [phase, guess1Correct, revealedCards]); // eslint-disable-line react-hooks/exhaustive-deps

  // FLIP animation
  useLayoutEffect(() => {
    if (phase !== "after2" || flipDone) return;
    let maxDelay = 0;
    correctOrder.forEach((item) => {
      const el = cardRefMap.current[item.label];
      const oldTop = snapPositions.current[item.label];
      if (!el || oldTop === undefined) return;
      const newTop = el.getBoundingClientRect().top;
      const delta = oldTop - newTop;
      if (delta === 0) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${delta}px)`;
      el.getBoundingClientRect();
      const delay = Math.abs(delta) > 5 ? 60 : 0;
      maxDelay = Math.max(maxDelay, delay + 420);
      el.style.transition = `transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms`;
      el.style.transform = "translateY(0)";
    });
    setTimeout(() => {
      setFlipDone(true);
      correctOrder.forEach((_, idx) => {
        setTimeout(() => { setRevealedCards(prev => [...prev, idx]); }, idx * 150);
      });
    }, maxDelay || 420);
  }, [phase, flipDone]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDragStart = (e, i) => { dragFrom.current = i; e.dataTransfer.effectAllowed = "move"; setIsDragging(true); setDragOverIdx(i); setDragSrcLabel(order[i].label); };
  const onDragOver = (e, i) => {
    e.preventDefault(); setDragOverIdx(i);
    if (!dragSrcLabel) return;
    setOrder(prev => { const from = prev.findIndex(item => item.label === dragSrcLabel); if (from === -1 || from === i) return prev; return reorderUnlocked(prev, from, i); });
  };
  const onTouchStart = (e, i) => { dragFrom.current = i; setDragSrcLabel(order[i]?.label ?? null); setIsDragging(true); setDragOverIdx(i); };
  const onTouchMove = (e) => {
    e.preventDefault();
    if (!dragSrcLabel) return;
    const { clientY } = e.touches[0];
    for (let i = 0; i < itemRefs.current.length; i++) {
      const el = itemRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY >= r.top && clientY <= r.bottom) {
        setDragOverIdx(i);
        setOrder(prev => { const from = prev.findIndex(item => item.label === dragSrcLabel); if (from === -1 || from === i) return prev; return reorderUnlocked(prev, from, i); });
        break;
      }
    }
  };

  const submit = () => {
    if (!startedRef.current) { startedRef.current = true; if (onStarted) onStarted(); }
    const correct = countCorrect(order);
    if (phase === "ordering") {
      if (correct === 4) {
        order.forEach(item => { const el = cardRefMap.current[item.label]; if (el) snapPositions.current[item.label] = el.getBoundingClientRect().top; });
        setLastGuess([...order]); setFlipDone(false); setRevealedCards([]);
        setRoundResults(r => [...r, "perfect"]); setPhase("after2");
      } else {
        const correctLabels = order.filter((item, i) => item.label === correctOrder[i].label).map(item => item.label);
        setGuess1Order(order.map(item => item.label));
        setGuess1Correct(correctLabels); setGuess1Count(correct); setPhase("after1");
      }
    } else {
      if (guess1Order && order.map(item => item.label).join(",") === guess1Order.join(",")) {
        setDupGuessMsg(true);
        setTimeout(() => setDupGuessMsg(false), 1500);
        return;
      }
      order.forEach(item => { const el = cardRefMap.current[item.label]; if (el) snapPositions.current[item.label] = el.getBoundingClientRect().top; });
      setLastGuess([...order]); setFlipDone(false); setRevealedCards([]);
      setRoundResults(r => [...r, correct === 4 ? "second" : "wrong"]); setPhase("after2");
    }
  };

  const nextRound = () => {
    const next = round + 1;
    if (next < TOTAL_ROUNDS) {
      setRound(next); setOrder(shuffle(puzzle.rounds[next].items)); setPhase("ordering");
      setGuess1Count(null); setGuess1Correct([]); setGuess1Order(null); setDupGuessMsg(false); setLastGuess(null);
      setRevealedCards([]); setFlipDone(false); snapPositions.current = {}; itemRefs.current = []; labelRefs.current = [];
    } else {
      onGameComplete(roundResults);
    }
  };

  const canDrag = phase === "ordering" || phase === "after1";
  const displayItems = phase === "after2" ? correctOrder : order;
  const isLockedCorrect = (i) => phase === "after1" && guess1Correct.includes(displayItems[i]?.label);
  const currentResult = roundResults[round];

  const pips = (
          <div style={{ display: "flex", gap: 6, justifyContent: "center", marginTop: 14 }}>
            {Array(TOTAL_ROUNDS).fill(0).map((_, i) => {
              let bg = C.border;
              if (i < roundResults.length) { const r = roundResults[i]; bg = r === "perfect" ? C.green : r === "second" ? C.yellow : C.red; }
              else if (i === round) bg = C.accent;
              return <div key={i} style={{ width: 30, height: 5, borderRadius: 3, background: bg, transition: "background 0.4s" }} />;
            })}
          </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: "24px 16px" }}>
      <div style={{ maxWidth: 460, width: "100%" }}>

        {renderHeader(pips)}

        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: C.text, whiteSpace: "nowrap" }}>Round {round + 1} of {TOTAL_ROUNDS}</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>
          <div style={{ lineHeight: 1.2, marginBottom: 10 }}>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 18, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: C.muted }}>Rank by </span>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 26, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: C.text }}>{currentRound.question}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 19, fontWeight: 600, color: C.accent, letterSpacing: 0.5 }}>{currentRound.hint}</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>
        </div>

        <div style={{ marginBottom: 12, minHeight: 46 }}>
          {dupGuessMsg ? (
            <div style={{ display: "flex", alignItems: "center", padding: "11px 14px", background: "rgba(232,80,80,0.07)", border: `1px solid rgba(232,80,80,0.25)`, borderRadius: 6 }}>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: C.red, letterSpacing: 1, textTransform: "uppercase" }}>Already guessed this order — move a card first</span>
            </div>
          ) : phase === "after1" && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", background: "rgba(212,168,50,0.07)", border: `1px solid rgba(212,168,50,0.25)`, borderRadius: 6 }}>
              <div style={{ display: "flex", gap: 5 }}>
                {guess1Count === 0
                  ? <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: C.muted }}>—</span>
                  : Array(guess1Count).fill(0).map((_, i) => <div key={i} style={{ width: 14, height: 14, borderRadius: 3, background: C.green }} />)
                }
              </div>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: guess1Count === 0 ? C.red : C.yellow, letterSpacing: 1, textTransform: "uppercase" }}>
                {guess1Count === 0 ? "0/4 correct · one more shot" : `${guess1Count}/4 correct · one more shot`}
              </span>
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 0, marginBottom: 14, position: "relative" }}>
          {displayItems.map((item, i) => {
            const locked = isLockedCorrect(i);
            const cardCanDrag = canDrag && !guess1Correct.includes(item.label);
            let borderCol = C.border, bgCol = C.surface, numCol = C.muted;
            if (locked) {
              borderCol = C.green; bgCol = "rgba(62,200,122,0.08)"; numCol = C.green;
            } else if (phase === "after2" && lastGuess) {
              const ok = lastGuess[i]?.label === correctOrder[i].label;
              const revealed = revealedCards.includes(i);
              if (revealed) {
                borderCol = ok ? C.green : C.red;
                bgCol = ok ? "rgba(62,200,122,0.06)" : "rgba(232,80,80,0.06)";
                numCol = ok ? C.green : C.red;
              }
            }
            const isPickedUp = cardCanDrag && isDragging && dragSrcLabel === item.label;
            const isDropTarget = cardCanDrag && isDragging && dragOverIdx === i && dragSrcLabel !== item.label;
            const isDimmed = isDragging && dragSrcLabel !== item.label && !locked;
            return (
              <div key={item.label} style={{ height: rowH, position: "relative", marginBottom: ROW_GAP }}>
                <div style={{ position: "absolute", top: -4, left: 0, right: 0, height: isDropTarget ? 3 : 0, background: C.accent, borderRadius: 2, opacity: isDropTarget ? 1 : 0, transition: "height 0.1s, opacity 0.1s", zIndex: 20 }} />
                <div
                  ref={el => { itemRefs.current[i] = el; cardRefMap.current[item.label] = el; }}
                  draggable={cardCanDrag}
                  onDragStart={cardCanDrag ? e => onDragStart(e, i) : undefined}
                  onDragOver={canDrag ? e => onDragOver(e, i) : undefined}
                  onDragEnd={cardCanDrag ? () => { dragFrom.current = null; setIsDragging(false); setDragOverIdx(null); setDragSrcLabel(null); } : undefined}
                  onDrop={canDrag ? e => e.preventDefault() : undefined}
                  onTouchStart={cardCanDrag ? e => onTouchStart(e, i) : undefined}
                  onTouchMove={cardCanDrag ? onTouchMove : undefined}
                  onTouchEnd={cardCanDrag ? () => { dragFrom.current = null; setIsDragging(false); setDragOverIdx(null); setDragSrcLabel(null); } : undefined}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: isPickedUp ? "rgba(212,168,50,0.15)" : isDropTarget ? "rgba(212,168,50,0.08)" : bgCol,
                    border: `${isPickedUp ? "2px" : "1px"} solid ${isPickedUp ? C.accent : isDropTarget ? C.accent : borderCol}`,
                    borderRadius: 6, padding: isPickedUp ? "0 15px" : "0 16px",
                    cursor: locked ? "default" : cardCanDrag ? "grab" : "default",
                    touchAction: cardCanDrag ? "none" : "auto",
                    userSelect: "none", WebkitUserSelect: "none",
                    transition: phase === "after2" ? "background 0.35s, border-color 0.35s" : "all 0.15s ease",
                    position: "absolute", top: 0, bottom: 0, left: 0, right: 0,
                    transform: isPickedUp ? "scale(1.04) translateY(-2px)" : "scale(1)",
                    boxShadow: isPickedUp ? `0 8px 24px rgba(212,168,50,0.35), 0 4px 8px rgba(0,0,0,0.4)` : "none",
                    opacity: isDimmed ? 0.55 : 1,
                    zIndex: isPickedUp ? 10 : 1,
                  }}
                >
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, fontWeight: 700, color: numCol, minWidth: 20, flexShrink: 0, transition: "color 0.3s" }}>{i + 1}</span>
                  <span ref={el => { labelRefs.current[i] = el; }} style={{ flex: 1, minWidth: 0, color: C.text, fontSize: 16, fontWeight: 500, lineHeight: 1.3, overflowWrap: "anywhere" }}>{item.label}</span>
                  {locked
                    ? <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 15, color: C.green, flexShrink: 0, textAlign: "right" }}>{item.display}</span>
                    : phase === "after2" && revealedCards.includes(i)
                    ? <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 15, color: numCol, flexShrink: 0, textAlign: "right" }}>{item.display}</span>
                    : phase !== "after2"
                    ? <svg width="14" height="22" viewBox="0 0 14 22" style={{ opacity: cardCanDrag ? 0.6 : 0.18, flexShrink: 0 }}>
                        {[[2,2],[8,2],[2,8],[8,8],[2,14],[8,14],[2,20],[8,20]].map(([x,y]) => (
                          <circle key={`${x}${y}`} cx={x} cy={y} r="2.2" fill={cardCanDrag ? C.accent : C.muted} />
                        ))}
                      </svg>
                    : null
                  }
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ height: 110 }}>
          <div style={{ display: canDrag ? "block" : "none" }}>
            <button onClick={submit} style={{ width: "100%", background: C.accent, color: "#000", border: "none", borderRadius: 6, padding: "18px", fontSize: 17, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              {phase === "after1" ? "Final Answer" : "Lock It In"}
            </button>
            <div style={{ textAlign: "center", marginTop: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 13, letterSpacing: 0.5, color: "#AAAAAA" }}>
              {phase === "after1" ? "Rearrange and lock it in · last chance" : "Drag to reorder · 2 guesses per round"}
            </div>
          </div>
          <div style={{ display: phase === "after2" ? "flex" : "none", flexDirection: "column", gap: 10 }}>
            <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
              <span style={{ fontSize: 20, marginRight: 8 }}>
                {currentResult === "perfect" ? "🟩" : currentResult === "second" ? "🟨" : "🟥"}
              </span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: currentResult !== "wrong" ? C.green : C.red }}>
                {currentResult === "perfect" ? "First go. Perfect." : currentResult === "second" ? "Got there on the second try." : "Not this time — correct order above."}
              </span>
            </div>
            <button onClick={nextRound} style={{ width: "100%", background: "transparent", color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "15px", fontSize: 15, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              {round < TOTAL_ROUNDS - 1 ? "Next Round →" : "See Results"}
            </button>
          </div>
        </div>

        {footer}
      </div>
    </div>
  );
}

function ArchiveGameScreen({ puzzle, dayNum, existingResult, user, onBack, onSaved }) {
  const [finishedResults, setFinishedResults] = useState(null);

  const calDate = getPuzzleDayDate(dayNum);
  const dayLabel = `Day ${dayNum} · ${calDate.getDate()} ${MONTH_SHORT[calDate.getMonth()]}`;

  const saveArchiveResult = async (score, rr) => {
    if (!user) return;
    try {
      const { data: existing } = await supabase
        .from("results").select("id").eq("user_id", user.id).eq("day", dayNum).maybeSingle();
      if (existing) {
        await supabase.from("results")
          .update({ score, round_results: rr, played_at: new Date().toISOString() })
          .eq("user_id", user.id).eq("day", dayNum);
      } else {
        await supabase.from("results")
          .insert({ user_id: user.id, day: dayNum, score, round_results: rr, played_at: new Date().toISOString() });
      }
      if (onSaved) onSaved();
    } catch (err) {
      console.error("[archive save] failed", err);
    }
  };

  // Save once GameEngine reports the finished game (results only — archive games never touch streaks)
  useEffect(() => {
    if (finishedResults && finishedResults.length === TOTAL_ROUNDS) {
      const score = finishedResults.filter(r => r !== "wrong").length;
      saveArchiveResult(score, finishedResults);
    }
  }, [finishedResults]); // eslint-disable-line react-hooks/exhaustive-deps

  if (finishedResults || existingResult) {
    const displayResults = existingResult ? existingResult.round_results : finishedResults;
    return <ArchiveResultsView puzzle={puzzle} dayNum={dayNum} roundResults={displayResults} onBack={onBack} />;
  }

  return (
    <GameEngine
      puzzle={puzzle}
      renderHeader={(pips) => (
        <div style={{ textAlign: "center", marginBottom: 24, position: "relative" }}>
          <button onClick={onBack} style={{ position: "absolute", left: 0, top: 8, fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.muted, background: "none", border: "none", cursor: "pointer", padding: 0 }}>← Back</button>
          <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 44, fontWeight: 700, color: C.accent, letterSpacing: "-1px", lineHeight: 1 }}>PECKING ORDER</div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: C.muted, letterSpacing: 2, marginTop: 6, textTransform: "uppercase" }}>{dayLabel}</div>
          {pips}
        </div>
      )}
      onGameComplete={(rr) => setFinishedResults(rr)}
    />
  );
}

function PrivacyScreen({ onBack }) {
  const isMagicLinkReturn = window.location.hash.includes("type=magiclink") || window.location.hash.includes("access_token");
  const [phase, setPhase] = useState(isMagicLinkReturn ? "confirming" : "idle");
  // idle | submitting | sent | confirming | deleting | deleted | error
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!isMagicLinkReturn) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        setPhase("deleting");
        const userId = session.user.id;
        try {
          const { error: re } = await supabase.from("results").delete().eq("user_id", userId);
          const { error: se } = await supabase.from("streaks").delete().eq("user_id", userId);
          if (re || se) {
            console.error("[delete] results:", re, "streaks:", se);
            setPhase("error");
            return;
          }
          window.history.replaceState({}, "", "/privacy");
          await supabase.auth.signOut();
          setPhase("deleted");
        } catch (err) {
          console.error("[delete] failed:", err);
          setPhase("error");
        }
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestDeletion = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setPhase("submitting");
    setErrorMsg("");
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: { shouldCreateUser: false, redirectTo: "https://peckingorder.club/privacy" },
    });
    if (error) {
      setErrorMsg("No account found for that email address.");
      setPhase("idle");
    } else {
      setPhase("sent");
    }
  };

  const S = { // local style shortcuts
    page: { minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans', sans-serif", padding: "40px 20px", color: C.text },
    wrap: { maxWidth: 560, margin: "0 auto" },
    logo: { fontFamily: "'Fredoka', sans-serif", fontSize: 32, fontWeight: 700, color: C.accent, letterSpacing: "-0.5px", marginBottom: 6 },
    back: { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.muted, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 28, display: "block" },
    h1: { fontFamily: "'Fredoka', sans-serif", fontSize: 26, fontWeight: 700, color: C.text, marginBottom: 24, marginTop: 16 },
    h2: { fontFamily: "'Fredoka', sans-serif", fontSize: 18, fontWeight: 700, color: C.accent, marginTop: 24, marginBottom: 8 },
    p: { fontSize: 15, lineHeight: 1.7, color: "#C8C0A8", marginBottom: 8 },
    divider: { height: 1, background: C.border, margin: "32px 0" },
    label: { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 3, textTransform: "uppercase", color: C.muted, marginBottom: 10, display: "block" },
    input: { width: "100%", background: "#1A1A1A", border: `1px solid ${C.border}`, borderRadius: 6, padding: "14px 16px", fontSize: 15, color: C.text, fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box", outline: "none" },
    btn: { width: "100%", background: C.accent, color: "#000", border: "none", borderRadius: 6, padding: "16px", fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: "uppercase", cursor: "pointer", fontFamily: "'DM Mono', monospace", marginTop: 12 },
    notice: (col) => ({ padding: "14px 16px", background: "rgba(212,168,50,0.07)", border: `1px solid rgba(212,168,50,0.2)`, borderRadius: 6, fontSize: 14, color: col || C.text, lineHeight: 1.6, marginTop: 12 }),
  };

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.logo}>PECKING ORDER</div>
        <button onClick={onBack} style={{ ...S.back, display: "inline-block" }}>← Back to game</button>

        <h1 style={S.h1}>Privacy Policy</h1>
        <p style={S.p}>Pecking Order is a free daily puzzle game. Here's exactly what we collect and why.</p>

        <h2 style={S.h2}>What we collect</h2>
        <p style={S.p}>Your Google account email, your game results (score and round outcomes per day), your streak data, and anonymous page view data via Umami analytics.</p>

        <h2 style={S.h2}>What we don't collect</h2>
        <p style={S.p}>Passwords, payment information, location data, or anything else from your Google account beyond your email.</p>

        <h2 style={S.h2}>Where it's stored</h2>
        <p style={S.p}>Game results and streaks are stored in Supabase in Sydney, Australia. Analytics are stored with Umami Cloud.</p>

        <h2 style={S.h2}>Who can see your data</h2>
        <p style={S.p}>Only you. Your results are protected by row-level security — no other user or third party can read your data.</p>

        <h2 style={S.h2}>Deleting your data</h2>
        <p style={S.p}>Use the form below to request deletion. You'll receive a confirmation email before anything is removed.</p>

        <div style={S.divider} />

        <span style={S.label}>Request Data Deletion</span>

        {phase === "idle" || phase === "submitting" ? (
          <>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === "Enter" && requestDeletion()}
              style={S.input}
              disabled={phase === "submitting"}
            />
            {errorMsg && <div style={{ color: C.red, fontFamily: "'DM Mono', monospace", fontSize: 11, marginTop: 10, letterSpacing: 1 }}>{errorMsg}</div>}
            <button onClick={requestDeletion} disabled={phase === "submitting"} style={{ ...S.btn, opacity: phase === "submitting" ? 0.6 : 1 }}>
              {phase === "submitting" ? "Sending..." : "Send Confirmation Email"}
            </button>
          </>
        ) : phase === "sent" ? (
          <div style={S.notice()}>
            Check your email for a confirmation link. Click it to permanently delete your game data.
          </div>
        ) : phase === "confirming" || phase === "deleting" ? (
          <div style={S.notice()}>
            {phase === "deleting" ? "Deleting your data..." : "Verifying your identity..."}
          </div>
        ) : phase === "deleted" ? (
          <div style={{ ...S.notice(C.green), borderColor: "rgba(62,200,122,0.3)", background: "rgba(62,200,122,0.06)" }}>
            Your data has been deleted. All game results and streak data have been removed from our servers.
          </div>
        ) : phase === "error" ? (
          <div style={{ ...S.notice(C.red), borderColor: "rgba(232,80,80,0.3)", background: "rgba(232,80,80,0.06)" }}>
            Something went wrong. Please try again or contact us at <a href="mailto:hello@peckingorder.club" style={{ color: C.accent }}>hello@peckingorder.club</a>.
          </div>
        ) : null}

        <div style={{ marginTop: 48, paddingTop: 24, borderTop: `1px solid ${C.border}`, fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 2, color: "#555", textTransform: "uppercase" }}>
          Last updated April 2026 · <a href="mailto:hello@peckingorder.club" style={{ color: "#555", textDecoration: "none" }}>hello@peckingorder.club</a>
        </div>
      </div>
    </div>
  );
}

const devMode = process.env.NODE_ENV === "development" && new URLSearchParams(window.location.search).get("dev") === "1";

function OrderUp({ onPrivacy }) {
  const dayIndex = getDayIndex();
  const puzzle = ALL_PUZZLES[dayIndex];
  const todayPuzzleDay = puzzle.day;

  // Auth state
  const [user, setUser] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [userResults, setUserResults] = useState([]);
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // View state
  const [view, setView] = useState("game"); // "game" | "profile" | "stats"
  const [selectedArchiveDay, setSelectedArchiveDay] = useState(null);
  const [reviewingToday, setReviewingToday] = useState(false);

  // Game state — restore from localStorage if returning from Google OAuth redirect
  const _pending = (() => { try { const p = localStorage.getItem("po_pending_result"); return p ? JSON.parse(p) : null; } catch { return null; } })();
  const _hasToday = _pending && _pending.day === todayPuzzleDay;
  const [phase, setPhase] = useState(_hasToday ? "complete" : "ordering"); // "ordering" (GameEngine playing) | "complete"
  const [roundResults, setRoundResults] = useState(_hasToday ? _pending.roundResults : []);
  const [shareState, setShareState] = useState("idle");
  const gameStartedRef = useRef(false); // set by GameEngine on first submit — gates replay-restore and the day lock

  // Load user data
  const loadUserData = async (userId) => {
    const fetchTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("loadUserData timed out")), 8000)
    );
    try {
      const [{ data: streak, error: sErr }, { data: results, error: rErr }] = await Promise.race([
        Promise.all([
          supabase.from("streaks").select("*").eq("user_id", userId).maybeSingle(),
          supabase.from("results").select("day, score, round_results").eq("user_id", userId),
        ]),
        fetchTimeout,
      ]);
      if (sErr) console.error("[loadUserData] streaks fetch FAILED", sErr);
      if (rErr) console.error("[loadUserData] results fetch FAILED", rErr);
      if (streak) setStreakData(streak);
      if (results) setUserResults(results);
      return results ?? [];
    } catch (err) {
      console.error("Failed to load user data", err);
      return [];
    } finally {
      setUserDataLoaded(true);
    }
  };

  // Save game result
  const saveGameResult = async (userId, score, results) => {
    try {
      // Check if a record already exists for this user+day, then insert or update
      // (avoids relying on a named unique constraint for upsert conflict detection)
      const { data: existing_result, error: checkError } = await supabase
        .from("results").select("id").eq("user_id", userId).eq("day", todayPuzzleDay).maybeSingle();
      if (checkError) console.error("[saveGameResult] existence check FAILED", checkError);
      let saveError;
      if (existing_result) {
        ({ error: saveError } = await supabase
          .from("results").update({ score, round_results: results, played_at: new Date().toISOString() })
          .eq("user_id", userId).eq("day", todayPuzzleDay));
      } else {
        ({ error: saveError } = await supabase
          .from("results").insert({ user_id: userId, day: todayPuzzleDay, score, round_results: results, played_at: new Date().toISOString() }));
      }
      if (saveError) { console.error("[saveGameResult] results save FAILED", saveError); }
      const { data: existing, error: streakFetchError } = await supabase.from("streaks").select("*").eq("user_id", userId).maybeSingle();
      if (streakFetchError) {
        console.error("[saveGameResult] streaks fetch FAILED — aborting streak update to avoid reset", streakFetchError);
        await loadUserData(userId);
        return;
      }
      let newCurrent = 1, newLongest = 1;
      if (existing) {
        const lastDay = existing.last_played_day;
        const expectedPrev = todayPuzzleDay === 1 ? ALL_PUZZLES.length : todayPuzzleDay - 1;
        if (lastDay === todayPuzzleDay) { await loadUserData(userId); return; }
        else if (lastDay === expectedPrev) newCurrent = (existing.current_streak || 0) + 1;
        else newCurrent = 1;
        newLongest = Math.max(existing.longest_streak || 1, newCurrent);
      }
      const { error: streakError } = await supabase.from("streaks").upsert(
        { user_id: userId, current_streak: newCurrent, longest_streak: newLongest, last_played_day: todayPuzzleDay },
        { onConflict: "user_id" }
      );
      if (streakError) { console.error("[saveGameResult] streaks upsert FAILED", streakError); }
      await loadUserData(userId);
    } catch (err) {
      console.error("[saveGameResult] unexpected exception", err);
    }
  };

  // Helper: load user data then flush any pending result that isn't already saved.
  // Load-first means the UI updates immediately without waiting for save operations.
  // Save only runs for the logged-out-then-signed-in case (result not yet in Supabase).
  const flushPendingAndLoad = async (userId) => {
    const savedResults = await loadUserData(userId);

    const pending = localStorage.getItem("po_pending_result");
    if (!pending) return;
    try {
      const { day, score, roundResults: rr } = JSON.parse(pending);
      if (day !== todayPuzzleDay) {
        localStorage.removeItem("po_pending_result");
        return;
      }
      // Only save if today's result isn't already in Supabase
      const alreadySaved = savedResults.some(r => r.day === todayPuzzleDay);
      if (!alreadySaved) {
        await saveGameResult(userId, score, rr);
      }
      // Leave today's pending in localStorage so next open shows CompleteScreen instantly
    } catch (e) {
      console.error("[flushPendingAndLoad] failed to flush pending result", e);
      localStorage.removeItem("po_pending_result");
    }
  };

  // Auth init — rely solely on onAuthStateChange which fires immediately with
  // INITIAL_SESSION on subscription (covers already-logged-in case). getSession()
  // fires before the client has set its internal token, so we never flush from it.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      setAuthChecked(true); // auth state is known — show the game, load data in background
      if (u) {
        if (_event === "SIGNED_IN") {
          if (window.umami) umami.track('signup-completed');
        }
        flushPendingAndLoad(u.id);
      } else {
        setUserDataLoaded(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Replay detection / cross-device sync: once Supabase data arrives, restore today's result
  useEffect(() => {
    if (!userDataLoaded || !user) return;
    if (phase !== "ordering" || gameStartedRef.current) return;
    const todayResult = userResults.find(r => r.day === todayPuzzleDay);
    if (todayResult?.round_results?.length) {
      setRoundResults(todayResult.round_results);
      setPhase("complete");
    }
  }, [userDataLoaded, userResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save on complete
  useEffect(() => {
    if (phase === "complete" && roundResults.length === TOTAL_ROUNDS) {
      const score = roundResults.filter(r => r !== "wrong").length;
      // Persist to localStorage so Google OAuth redirect can flush it on return
      localStorage.setItem("po_pending_result", JSON.stringify({ day: todayPuzzleDay, score, roundResults }));
      if (user) {
        saveGameResult(user.id, score, roundResults);
      }
      if (window.umami) umami.track('game-completed', { day: todayPuzzleDay, score });
    }
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: 'https://peckingorder.club' },
    });
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    localStorage.removeItem("po_pending_result");
    setUser(null); setStreakData(null); setUserResults([]); setView("game");
  };

  // Tap delay + animation keyframes
  useEffect(() => {
    if (!document.getElementById("po-touch-style")) {
      const s = document.createElement("style");
      s.id = "po-touch-style";
      s.textContent = `
        * { -webkit-tap-highlight-color: transparent; }
        @keyframes slideIn {
          0%   { transform: translateX(-18px); opacity: 0; }
          60%  { transform: translateX(4px); opacity: 1; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes popIn {
          0%   { transform: scale(0.92); opacity: 0.4; }
          70%  { transform: scale(1.03); }
          100% { transform: scale(1); opacity: 1; }
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const handleCopy = async (quip) => {
    const score = roundResults.filter(r => r !== "wrong").length;
    const outcome = await shareText(buildShareText({ day: puzzle.day, roundResults, quip }));
    if (outcome === "cancelled") return;
    setShareState(outcome);
    setTimeout(() => setShareState("idle"), 2500);
    if (outcome !== "failed" && window.umami) {
      umami.track('result-shared', { day: todayPuzzleDay, score, via: outcome, quip: quip ? 1 : 0 });
    }
  };

  if (!authChecked) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 36, fontWeight: 700, color: C.accent, letterSpacing: "-0.5px" }}>PECKING ORDER</div>
    </div>
  );

  if (view === "profile") return (
    <ProfileScreen user={user} streakData={streakData} userResults={userResults} dataReady={userDataLoaded} onBack={() => setView("game")} onSignOut={signOut} onGoogleLogin={signInWithGoogle} todayPuzzleDay={todayPuzzleDay}
      onSelectDay={(day) => { setSelectedArchiveDay(day); setView("game"); }}
    />
  );

  if (selectedArchiveDay !== null) {
    const archivePuzzle = ALL_PUZZLES.find(p => p.day === selectedArchiveDay);
    const existingResult = userResults.find(r => r.day === selectedArchiveDay) || null;
    return (
      <ArchiveGameScreen
        puzzle={archivePuzzle}
        dayNum={selectedArchiveDay}
        existingResult={existingResult}
        user={user}
        onBack={() => { setSelectedArchiveDay(null); setView("profile"); }}
        onSaved={async () => { if (user) await loadUserData(user.id); }}
      />
    );
  }

  const hasPlayedToday = user && userDataLoaded && userResults.some(r => r.day === todayPuzzleDay);
  if (!devMode && hasPlayedToday && phase === "ordering" && !gameStartedRef.current) return (
    <LockedScreen puzzle={puzzle} userResults={userResults} todayPuzzleDay={todayPuzzleDay} user={user} streakData={streakData} onProfile={() => setView("profile")} onSignOut={signOut} />
  );

  if (phase === "complete" && reviewingToday) return (
    <RoundReviewScreen
      puzzle={puzzle} dayNum={todayPuzzleDay} roundResults={roundResults}
      onBack={() => setReviewingToday(false)} backLabel="Back to Results"
    />
  );

  if (phase === "complete") return (
    <CompleteScreen
      roundResults={roundResults} puzzle={puzzle} onCopy={handleCopy} shareState={shareState} dayIndex={dayIndex}
      user={user} streakData={streakData} userResults={userResults}
      onGoogleLogin={signInWithGoogle} onProfile={() => setView("profile")} onPrivacy={onPrivacy}
      onReview={() => setReviewingToday(true)}
    />
  );

  return (
    <GameEngine
      puzzle={puzzle}
      renderHeader={(pips) => (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <div style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 36, fontWeight: 700, color: C.accent, letterSpacing: "-0.5px", lineHeight: 1 }}>PECKING ORDER</div>
            <button
              onClick={() => setView("profile")}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", flexShrink: 0, opacity: user ? 1 : 0.4 }}
              title={user ? "Profile" : "Sign in to track your streak"}
            >
              <ProfileIcon color={user ? C.accent : C.muted} size={22} />
            </button>
          </div>

          {pips}
        </div>
      )}
      footer={
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <button onClick={onPrivacy} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 2, color: C.muted, background: "none", border: "none", cursor: "pointer", textTransform: "uppercase", padding: 0 }}>Privacy Policy</button>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 1, color: C.muted, marginTop: 6, opacity: 0.55 }}>build {APP_BUILD} · {ALL_PUZZLES.length} days</div>
        </div>
      }
      onStarted={() => { gameStartedRef.current = true; }}
      onGameComplete={(rr) => { setRoundResults(rr); setPhase("complete"); }}
    />
  );
}

export default function App() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const handler = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const navigateTo = (to) => { window.history.pushState({}, "", to); setPath(to); };

  if (path === "/privacy") return <PrivacyScreen onBack={() => navigateTo("/")} />;
  return <OrderUp onPrivacy={() => navigateTo("/privacy")} />;
}